import { ResultTemplatesHelpers, HighlightUtils } from "@coveo/headless";
import parse from "html-react-parser";
import { InteractiveResult } from "components/coveo-headless/components/InteractiveResult";
import SearchPageActions from "components/search/client/SearchPageActions";
import ButtonIcon from "components/utility/button/ButtonIcon";
import { highlightTitle } from "../utils/highlightTitle";

//helper function for public site search results page
const setPublicSearchEyebrow = (rawDetails) => {
	let eyebrow = "";
	if (rawDetails.resulttype === "BioPage") {
		eyebrow = "Biographies";
	} else {
		if (rawDetails.microsite === "insights") {
			eyebrow = rawDetails.tagcategory ?? rawDetails.resultformat;
		}
		if (rawDetails.microsite === "newsroom") {
			eyebrow = rawDetails.tagseries ?? rawDetails.resultformat;
		}
	}

	if (eyebrow) {
		return (
			<div className="public-search-result-card-eyebrow t-label-xxs tc-cool-gray-02">{eyebrow}</div>
		);
	}
	return null;
};

const getAuthorDateTime = (resultRaw) => {
	return (
		<>
			{resultRaw.byline ? resultRaw.byline : ""}
			{resultRaw.showpublishdate === "1" && resultRaw.publishdate
				? resultRaw.byline
					? ` | ${resultRaw.publishdate}`
					: resultRaw.publishdate
				: ""}
			{resultRaw.resulttimetoread
				? resultRaw.byline || (resultRaw.showpublishdate === "1" && resultRaw.publishdate)
					? ` | ${resultRaw.resulttimetoread}`
					: resultRaw.resulttimetoread
				: ""}
		</>
	);
};
/**
 * Order of operations:
 * 1 - convertStringToJSX
 * 2 - renderHTMLString
 * 3 - parse html string to react jsx
 * @param {} templates string of html
 * @param {*} headlessEngine coveo-headless engine
 * @returns react jsx
 */
const generateResultsJSXElements = (templates, headlessEngine) => {
	/**
	 * Replace template string variable with value from
	 * result object
	 * ${result.title} => "Is It Time To Sell Stocks?"
	 */
	const renderHTMLString = (str, obj) => {
		return str.replace(/\$\{(.+?)\}/g, (_match, p1) => findValueInObj(obj, p1));
	};

	/**
	 * Find the value in the result object that needs to replace the template variable
	 * example: variable=resulttitle, value="Dimensional On Markets Rewarding Discipline"
	 */
	const findValueInObj = (obj, valueToReplace) => {
		// take the template value and split it:
		// result.raw.resulttitle => ["result", "raw", "resulttitle"]
		// result.raw.publishdate => ["result", "raw", "publishdate"]
		if (typeof valueToReplace === "string") valueToReplace = valueToReplace.split(".");
		// if we've shifted all values out of the array of strings,
		// return the value mapped to the object,
		// example: obj = Aug 02, 2021, valueToReplace = []
		if (valueToReplace.length === 0) {
			return obj;
		} else {
			// remove first element of array
			// prop = "result"
			const prop = valueToReplace.shift();
			// if valueToReplace has items left, run again
			// run 1: obj["result"], valueToReplace = ["raw", "publishdate"]
			// run 2: obj["raw"], valueToReplace = ["publishdate"]
			// run 3: obj["publishdate"], valueToReplace = []
			// if key does NOT exist in obj, return empty string
			if (obj[prop] === undefined) return "";
			return findValueInObj(obj[prop], valueToReplace);
		}
	};

	/**
	 * Template will be a string of html elements with variables prefixed with result:
	 * (result.title, result.raw.resultthumbnail, result.raw.browsedescription)
	 * Replace the variables with jsx type variable declarations (${result.raw.resultthumbnail})
	 * Use html-react-parser to generate react jsx
	 */
	const convertStringToJSX = (contentString, result) => {
		//{result[variable]}
		const regex = /{(result\..*?)}/g;
		const decodedString = decodeURI(contentString);
		// <div class="class">${result.title}</div>
		const cleanString = decodedString.replace(regex, (_match, p1) => {
			return `\${${p1}}`;
		});
		// an card on insights or homepage of my.dimensional is an anchor tag,
		// use recursion to grab the anchor tag children and convert them into jsx
		// then insert children into <InteractiveResult>
		const cardRegex = /(?:<a class="coveo-headless-content-card.*?>)(.*?)(?=<\/a>)/;
		const cardsChildren = cleanString.match(cardRegex);
		// on search results page for my.dimensional images and videos are clickable links
		// use recursion to grab the anchor tag wrapping the image and convert into jsx
		// then insert into <InteractiveResult>
		const searchResultImgRegex = /(?:<a class=".*?search-result-image-link.*?>)(.*?)(?=<\/a>)/;
		const searchResultImg = cleanString.match(searchResultImgRegex);
		// replace certain elements from template with React JSX
		// These elements have event handlers that cannot be put into a template string
		// but are needed for coveo analytics
		const analytics = {
			"data-a-evt": "Link Click",
			"data-a-comp": "Content Card",
			"data-a-name": result.title
		};

		const videoFilters = new Set(["video", "webcast", "webcast highlight"]);

		const options = {
			replace: (domNode) => {
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("public-search-result-card-content")
				) {
					return (
						<InteractiveResult
							attribs={domNode.attribs}
							headlessEngine={headlessEngine}
							result={result}
							analytics={{
								...analytics,
								"data-a-comp": `${
									result.isTopResult ? "Featured Search Result Card" : "Search Result Card"
								}`
							}}
							type="anchor"
						>
							{result.raw.resulttype === "FundDetail" ? (
								<div className="public-search-result-card-title t-heading-lg">
									{parse(
										highlightTitle(
											result.raw.resulttitle,
											headlessEngine.state.search.response.termsToHighlight,
											headlessEngine.state.search.response.phrasesToHighlight
										)
									)}
								</div>
							) : (
								<div className="public-search-result-card-left">
									{result.raw.ispublicsearchimageenabled === "1"
										? null
										: setPublicSearchEyebrow(result.raw)}
									<div className="public-search-result-card-title t-heading-lg">
										{parse(
											highlightTitle(
												result.raw.resulttitle,
												headlessEngine.state.search.response.termsToHighlight,
												headlessEngine.state.search.response.phrasesToHighlight
											)
										)}
									</div>
									{result.excerpt ? (
										<div className="public-search-result-card-desc t-body-xs">
											{!result.raw.ispublicsearchimageenabled &&
											(result.raw.microsite === "newsroom" ||
												result.raw.microsite === "insights") ? (
												<>
													<span className="t-body-xs-italic">{result.raw.publishdate}</span>
													<span> – </span>
												</>
											) : null}
											{headlessEngine.state.query.q
												? parse(
														HighlightUtils.highlightString({
															content: result.excerpt,
															highlights: result.excerptHighlights,
															openingDelimiter: "<span class='t-body-xs-heavy'>",
															closingDelimiter: "</span>"
														})
													)
												: result.raw.browsedescription}
										</div>
									) : null}
								</div>
							)}
							{result.raw.ispublicsearchimageenabled === "1" ? (
								<div className="public-search-result-card-right">
									<img src={result.raw.resultthumbnail} alt={result.raw.resulttitle} />
								</div>
							) : null}
						</InteractiveResult>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-actions")
				) {
					if (headlessEngine.state.searchHub === "CoveoSearch") {
						if (result.raw.buttoncanshare === "1") {
							return (
								<div className="search-result-actions">
									<SearchPageActions
										dataset={{
											hideDownload: result.raw.buttonhidedownload,
											hideFavorite: "true",
											hideShare: result.raw.hideshare,
											color: "default",
											isModule: "false",
											isGlobalSearch:
												window.profileNav.featureStatus.globalSearch.status.toString(),
											downloadUrl: result.raw.resultdownload,
											id: result.raw.permanentid,
											title: result.raw.resulttitle
										}}
										headlessEngine={headlessEngine}
										result={result}
									/>
								</div>
							);
						} else {
							return (
								<div className="search-result-actions">
									<SearchPageActions
										dataset={{
											hideDownload: result.raw.buttonhidedownload,
											hideFavorite: "true",
											hideShare: result.raw.hideshare,
											color: "default",
											isModule: "false",
											isGlobalSearch:
												window.profileNav.featureStatus.globalSearch.status.toString(),
											downloadUrl: result.raw.resultdownload,
											guid: `${result.raw.tagaccesslevel}${result.raw.taguseid}`,
											title: result.raw.resulttitle
										}}
										headlessEngine={headlessEngine}
										result={result}
									/>
								</div>
							);
						}
					}
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("button-share")
				) {
					return (
						<SearchPageActions
							dataset={{
								hideDownload: "true",
								hideShare: "false",
								hideFavorite: "true",
								color: "default",
								isModule: "true",
								id: result.raw.permanentid
							}}
						/>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class === "media-center-result-list-title-link"
				) {
					return (
						<InteractiveResult
							attribs={domNode.attribs}
							headlessEngine={headlessEngine}
							result={result}
							type="anchor"
						>
							{result.title}
						</InteractiveResult>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("coveo-headless-content-card")
				) {
					return (
						<InteractiveResult
							attribs={domNode.attribs}
							headlessEngine={headlessEngine}
							result={result}
							analytics={analytics}
							type="anchor"
						>
							{cardsChildren.length > 1
								? parse(renderHTMLString(cardsChildren[1], { result: result }))
								: null}
						</InteractiveResult>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class === "media-center-result-list-media"
				) {
					return (
						<div className="media-center-result-list-media">
							{result.raw.resultthumbnail ? (
								<img
									className="media-center-result-list-media-img"
									src={result.raw.resultthumbnail}
									alt={result.title}
								/>
							) : (
								""
							)}

							<div className="media-center-result-list-media-description">
								{result.raw.browsedescription}
							</div>
						</div>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class === "media-center-result-list-media-video"
				) {
					return (
						<div className="media-center-result-list-media">
							{result.raw.resultthumbnail ? (
								<div className="media-center-result-list-media-vidContainer">
									<img
										className="media-center-result-list-media-vidContainer-vid"
										src={result.raw.resultthumbnail}
										alt={result.title}
									/>
									<ButtonIcon type="play" color="teal" />
								</div>
							) : (
								""
							)}

							<div className="media-center-result-list-media-description">
								{result.raw.browsedescription}
							</div>
						</div>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-title-link")
				) {
					return (
						<InteractiveResult
							attribs={domNode.attribs}
							headlessEngine={headlessEngine}
							result={result}
							type="anchor"
							analytics={
								result.isTopResult
									? {
											"data-a-evt": "Link Click",
											"data-a-name": result.raw.resulttitle,
											"data-a-comp": "Featured Search Result Card"
										}
									: null
							}
						>
							{parse(
								highlightTitle(
									result.raw.resulttitle,
									headlessEngine.state.search.response.termsToHighlight,
									headlessEngine.state.search.response.phrasesToHighlight
								)
							)}
						</InteractiveResult>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-image-link")
				) {
					if (result.isTopResult) {
						if (videoFilters.has(result.raw.resultformat.toLowerCase())) {
							// Video
							if (!domNode.attribs.class.includes("media-center-result-list-media-vidContainer")) {
								// Removes image section present in featured card template if the layout should mimic video
								return <></>;
							}
						} else if (result.raw.browsecardtype.toLowerCase() === "image") {
							/// Image
							if (!domNode.attribs.class.includes("media-center-result-list-media-vidContainer")) {
								// Removes video section present in featured card template if the layout should mimic image
								return <></>;
							}
							return (
								<InteractiveResult
									attribs={domNode.attribs}
									headlessEngine={headlessEngine}
									result={result}
									type="anchor"
								>
									{searchResultImg
										? // Because searchResultImg grabs the first img it will include the play button
											// This removes the play button on the image component (it shouldn't have one)
											searchResultImg.length > 1
											? parse(renderHTMLString(searchResultImg[1], { result: result }), {
													replace: (domNode) => {
														if (
															domNode &&
															domNode.attribs &&
															domNode.attribs.class &&
															domNode.attribs.class.includes("button-icon")
														) {
															return <></>;
														}
													}
												})
											: null
										: null}
								</InteractiveResult>
							);
						} else {
							// Featured cards bring in all the nodes for video / images.
							// If it's not either of those, remove the nodes to just leave a description
							return <></>;
						}
					}

					return (
						<InteractiveResult
							attribs={domNode.attribs}
							headlessEngine={headlessEngine}
							result={result}
							type="anchor"
						>
							{searchResultImg
								? searchResultImg.length > 1
									? parse(renderHTMLString(searchResultImg[1], { result: result }))
									: null
								: null}
						</InteractiveResult>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-description")
				) {
					if (
						// Check if it's featured card and not a video card (which also reads has a format of description)
						// Adds " search-result-description-only" to remove padding from description
						result.isTopResult &&
						!videoFilters.has(result.raw.resultformat.toLowerCase()) &&
						result.raw.browsecardtype.toLowerCase() === "description"
					) {
						domNode.attribs.class += " search-result-description-only";
					}

					return (
						<div className={domNode.attribs.class}>
							{headlessEngine.state.query.q
								? parse(
										HighlightUtils.highlightString({
											content: result.excerpt,
											highlights: result.excerptHighlights,
											openingDelimiter: "<span>",
											closingDelimiter: "</span>"
										})
									)
								: result.raw.browsedescription}
						</div>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class === "search-result-date"
				) {
					return <div className="search-result-date">{getAuthorDateTime(result.raw)}</div>;
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-global-countries")
				) {
					return (
						<>
							{result.raw.tagapprovedcountries && (
								<div className="search-result-global-content search-result-global-countries">
									<div className="search-result-global-content-label">
										<p className="label-3-left-medium-cool-gray">
											{result.raw.tagapprovedcountries.length > 1
												? "Approved Countries"
												: "Approved Country"}
										</p>
									</div>
									<div className="search-result-global-content-items">
										<p className="body-3-left-almost-black">
											{result.raw.tagapprovedcountries.join(", ")}
										</p>
									</div>
								</div>
							)}
						</>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-global-countries")
				) {
					<>
						{result.raw.tagalllanguages && (
							<div className="search-result-global-content search-result-global-languages">
								<div className="search-result-global-content-label">
									<p className="label-3-left-medium-cool-gray">Language</p>
								</div>
								<div className="search-result-global-content-items">
									<p className="body-3-left-almost-black">{result.raw.tagalllanguages}</p>
								</div>
							</div>
						)}
					</>;
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-global-currency")
				) {
					return (
						<>
							{result.raw.tagcurrency && (
								<div className="search-result-global-content search-result-global-currency">
									<div className="search-result-global-content-label">
										<p className="label-3-left-medium-cool-gray">Currency</p>
									</div>
									<div className="search-result-global-content-items">
										<p className="body-3-left-almost-black">{result.raw.tagcurrency}</p>
									</div>
								</div>
							)}
						</>
					);
				}
				if (
					domNode.attribs &&
					domNode.attribs.class &&
					domNode.attribs.class.includes("search-result-global-topic")
				) {
					return (
						<>
							{result.raw.tagtopic && (
								<div className="search-result-global-content search-result-global-topic">
									<div className="search-result-global-content-label">
										<p className="label-3-left-medium-cool-gray">Topic</p>
									</div>
									<div className="search-result-global-content-items">
										<div className="search-result-global-topics">
											{result.raw.tagtopic.map((topic) => (
												<a
													key={topic}
													href={`/search/#sort=relevancy&f:@tagtopic=[${topic}]`}
													className="button-action button-action-outline button-action-onethird search-result-topic-button"
													target="_self"
													role="button"
													aria-label={topic}
													tabIndex={0}
													rel=""
												>
													<span className="button-action-show">{topic}</span>
												</a>
											))}
										</div>
									</div>
								</div>
							)}
						</>
					);
				}
			}
		};
		return <>{parse(renderHTMLString(cleanString, { result: result }), options)}</>;
	};

	return templates.map((template) => ({
		conditions:
			template.conditions.length === 0
				? []
				: template.conditions.map((condition) => {
						return ResultTemplatesHelpers.fieldMustMatch(condition.field, [
							...condition.value.split(",").map((v) => v.trim())
						]);
					}),
		content: (result) => convertStringToJSX(template.content, result)
	}));
};

export default generateResultsJSXElements;
